import React from 'react';
import { format, formatDistance } from 'date-fns';
import Link from 'next/link';
import MaterialLink from '@material-ui/core/Link';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import RoomIcon from '@material-ui/icons/Room';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Slider from 'react-slick';
import { useMediaQuery } from '@material-ui/core';

import useGetShortLink from 'src/hooks/api/listings/useGetShortLink';
import { useAuthState } from 'src/contexts/auth/auth.context';
import { formatPrice } from 'src/utils/helpers';
import { IListing } from 'src/interface';
import { BREAKPOINTS } from 'src/utils/constants';
import UserDetailsPanel from './Components/UserDetailsPanel';
import Tips from './Components/Tips';
import Report from './Components/Report';
import ShareListing from './Components/ShareListing';
import FavouriteListingToggle from './Components/FavoutieListingToggle';
import {
  PageContainer,
  PageContent,
  LeftSection,
  PriceBox,
  PriceAndLastUpdate,
  PriceText,
  PriceUpdateText,
  ProductTitle,
  RightSection,
  AdContent,
  SharingAdSection,
  FavouriteListing,
  IconTypography,
  SliderImage,
  DetailsContainer,
  DetailItemContainer,
  DetailItem,
  DetailAttribute,
  DetailInfo,
  DetailsSection,
  productBreadcrumbs,
  ListingDetailText,
  ListingDetailsContainer,
  StyledMoreAdsSlider,
  MainSliderSettings,
  Title,
} from './styles';
import CardsSlider from '../Common/CardSlider';

interface IProps {
  listing: IListing;
  related?: IListing[] | null;
  more?: IListing[] | null;
  isWantedListing?: boolean;
}

const ListingDetails = ({ listing, related, more, isWantedListing }: IProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const { shortLink } = useGetShortLink(listing);
  const authState = useAuthState();
  const { isAuthenticated } = authState;
  const {
    user,
    attributes,
    images,
    // medias,
    view_count,
    parent_category,
    child_category,
    location,
    title,
    short_description,
    updated_at,
  } = listing;
  const { first_name, last_name } = user;
  const {
    expected_price,
    condition,
    rental_period,
    rent_per,
    availability_from,
    availability_to,
    rental_value_per_period,
    is_rent_negotiable,
    other_attributes,
  } = attributes;

  const listingOtherAttributes = other_attributes && JSON.parse(other_attributes);

  return (
    <PageContainer maxWidth="lg">
      <PageContent isMobile={isMobile}>
        <LeftSection>
          <PriceBox>
            <PriceAndLastUpdate>
              <PriceText variant="h4">{`${formatPrice.format(
                rental_value_per_period
              )} per ${rent_per}`}</PriceText>
              <PriceUpdateText variant="h6">
                {`Last Updated: ${formatDistance(new Date(updated_at.split(' ')[0]), new Date(), {
                  addSuffix: true,
                })}
                `}
              </PriceUpdateText>
            </PriceAndLastUpdate>
          </PriceBox>
          <UserDetailsPanel user={user} context={title} showLinkToAllAds />
          <Tips />
          {isAuthenticated && <Report listingId={listing.id.toString()} />}
        </LeftSection>
        <RightSection isMobile={isMobile}>
          <AdContent>
            <Breadcrumbs
              style={productBreadcrumbs}
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              <Link href="/[parentCategory]" as={`/${parent_category.slug}`}>
                <MaterialLink color="primary">{parent_category.name}</MaterialLink>
              </Link>
              <Link
                href="/[parentCategory]/[childCategory]"
                as={`/${parent_category.slug}/${child_category.slug}`}
              >
                <MaterialLink color="primary">{child_category.name}</MaterialLink>
              </Link>
            </Breadcrumbs>
            <Divider />
            <ProductTitle variant="h3">{title}</ProductTitle>
            <Breadcrumbs style={productBreadcrumbs} separator="/" aria-label="breadcrumb">
              <Typography color="textPrimary">
                <span>Posted By: </span>
                <Link href="/user-listings/[id]" as={`/user-listings/${user.id}`} prefetch={false}>
                  <MaterialLink color="primary">{`${first_name} ${last_name}`}</MaterialLink>
                </Link>
              </Typography>
              <IconTypography color="textPrimary">
                <RoomIcon />
                <MaterialLink color="primary">
                  <address>{location.address}</address>
                </MaterialLink>
              </IconTypography>
              <IconTypography color="textPrimary">
                <VisibilityIcon /> <span style={{ marginLeft: 3 }}>{view_count}</span>
              </IconTypography>
            </Breadcrumbs>
          </AdContent>
          <SharingAdSection>
            {shortLink && <ShareListing link={shortLink} title={title} />}
            <FavouriteListing>
              <FavouriteListingToggle listing={listing} isWantedListing={isWantedListing} />
            </FavouriteListing>
          </SharingAdSection>
          <Slider {...MainSliderSettings}>
            {images.map((image, index) => {
              return (
                <SliderImage
                  key={index}
                  src={image}
                  width={(isMobile && 400) || 800}
                  height={400}
                  alt="Slider item"
                />
              );
            })}
          </Slider>
          <DetailsSection style={{ marginTop: 50 }}>
            <ListingDetailsContainer>
              <Title>Details</Title>
              <DetailsContainer>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Price</DetailAttribute>
                    <DetailInfo>{formatPrice.format(expected_price)}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Condition</DetailAttribute>
                    <DetailInfo>{condition}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Rent value</DetailAttribute>
                    <DetailInfo>{formatPrice.format(rental_value_per_period)}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Availability from</DetailAttribute>
                    <DetailInfo>{format(new Date(availability_from), 'dd/MM/yyyy')}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Rent negotiable</DetailAttribute>
                    <DetailInfo>{(is_rent_negotiable && 'Yes') || 'No'}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Availability to</DetailAttribute>
                    <DetailInfo>{format(new Date(availability_to), 'dd/MM/yyyy')}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Location</DetailAttribute>
                    <DetailInfo>{location.address}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
                <DetailItem>
                  <DetailItemContainer>
                    <DetailAttribute>Rental period</DetailAttribute>
                    <DetailInfo>{`${rental_period} days`}</DetailInfo>
                  </DetailItemContainer>
                </DetailItem>
              </DetailsContainer>
            </ListingDetailsContainer>
          </DetailsSection>
          {listingOtherAttributes && Object.keys(listingOtherAttributes).length > 0 && (
            <DetailsSection>
              <ListingDetailsContainer>
                <Title>Other details</Title>
                <DetailsContainer>
                  {listingOtherAttributes &&
                    Object.keys(listingOtherAttributes).map((key, i) => {
                      return (
                        <DetailItem key={i}>
                          <DetailItemContainer>
                            <DetailAttribute
                              style={{
                                textTransform: 'capitalize',
                              }}
                            >
                              {key.split('_').join(' ')}
                            </DetailAttribute>
                            <DetailInfo>{listingOtherAttributes[key]}</DetailInfo>
                          </DetailItemContainer>
                        </DetailItem>
                      );
                    })}
                </DetailsContainer>
              </ListingDetailsContainer>
            </DetailsSection>
          )}
          <DetailsSection>
            <Title>Description</Title>
            <ListingDetailText>{short_description}</ListingDetailText>
          </DetailsSection>
          {more && !isMobile && more.length > 0 && (
            <StyledMoreAdsSlider
              isWantedListing={isWantedListing}
              listings={more}
              title="More Ads by User"
              sliderSettings={{
                dots: false,
                slidesToShow: 3,
                slidesToScroll: 1,
              }}
            />
          )}
        </RightSection>
      </PageContent>
      {more && isMobile && more.length > 0 && (
        <StyledMoreAdsSlider
          isWantedListing={isWantedListing}
          listings={more}
          title="More Ads by User"
          sliderSettings={{
            dots: false,
            slidesToShow: 1,
            slidesToScroll: 1,
          }}
        />
      )}
      {related && related.length > 0 && (
        <CardsSlider
          isWantedListing={isWantedListing}
          listings={related}
          title="Related Ads"
          sliderSettings={{
            dots: false,
            slidesToShow: (isMobile && 1) || 4,
            slidesToScroll: (isMobile && 1) || 2,
          }}
        />
      )}
    </PageContainer>
  );
};
export default ListingDetails;
